import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Permission } from '@wms/shared';
import { apiFetch, ApiError } from './api';

interface IntendedOperation {
  readonly method: string;
  readonly path: string;
  readonly body: unknown;
}

interface PendingElevation {
  readonly scope: Permission;
  readonly operation: IntendedOperation;
  readonly resolve: (token: string) => void;
  readonly reject: (error: unknown) => void;
}

interface ElevationContextValue {
  requestToken: (scope: Permission, operation: IntendedOperation) => Promise<string>;
}

const ElevationContext = createContext<ElevationContextValue | null>(null);

/**
 * 全局提权对话框（架构书 §11.2）。挂载一次，任何组件通过 `useElevatedFetch()`
 * 触发——用户重新输入密码后签发的令牌只对**这一次**请求体有效（指纹绑定，
 * D-02），所以对话框必须知道"这次到底要批准哪个请求体"。
 *
 * M1 只实现密码重输这一条路径（`ELEVATED_PERMISSIONS`：user.manage /
 * override.pin.manage / stockcount.approve）。PIN 双主体路径（D-02）专属于
 * `inventory.override.negative`，M1 没有任何库存写入 UI 会触发它，留给
 * M6/M7 落地负库存 override 界面时再接上。
 */
export function ElevationProvider({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingElevation | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const pendingRef = useRef<PendingElevation | null>(null);

  const requestToken = useCallback(
    (scope: Permission, operation: IntendedOperation): Promise<string> => {
      return new Promise<string>((resolve, reject) => {
        const entry: PendingElevation = { scope, operation, resolve, reject };
        pendingRef.current = entry;
        setPending(entry);
        setPassword('');
        setError(null);
      });
    },
    [],
  );

  const close = (): void => {
    pendingRef.current?.reject(new Error('elevation_cancelled'));
    pendingRef.current = null;
    setPending(null);
  };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!pending) return;
    setSubmitting(true);
    setError(null);
    try {
      const { elevationToken } = await apiFetch<{ elevationToken: string }>('/elevation/grant', {
        method: 'POST',
        body: {
          scope: pending.scope,
          method: 'password',
          password,
          // 【sweep #2 M-1】把令牌绑定到具体目标操作：HTTP 方法 + 路径 + 请求体
          intendedMethod: pending.operation.method,
          intendedPath: pending.operation.path,
          intendedRequestBody: pending.operation.body,
        },
      });
      pending.resolve(elevationToken);
      pendingRef.current = null;
      setPending(null);
    } catch (err) {
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ElevationContext.Provider value={{ requestToken }}>
      {children}
      {pending && (
        <div className="modal-overlay" role="presentation" onClick={close}>
          <form
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={t('elevation.title')}
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => void submit(e)}
          >
            <h2>{t('elevation.title')}</h2>
            <label className="field">
              <span>{t('elevation.passwordLabel')}</span>
              <input
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            {error && <p className="field-error">{error}</p>}
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={close}
                disabled={submitting}
              >
                {t('app.cancel')}
              </button>
              <button type="submit" className="btn btn--primary" disabled={submitting || !password}>
                {t('elevation.confirm')}
              </button>
            </div>
          </form>
        </div>
      )}
    </ElevationContext.Provider>
  );
}

function useElevationContext(): ElevationContextValue {
  const ctx = useContext(ElevationContext);
  if (!ctx) throw new Error('useElevation must be used within ElevationProvider');
  return ctx;
}

/**
 * 需要提权的变更请求的统一入口：先按普通方式调用，被 403
 * AUTHZ.ELEVATION_REQUIRED 拒绝时弹出密码对话框，拿到令牌后用**同一个**
 * body 重放一次——`fn` 必须是幂等可重放的（所有变更端点本来就应该是）。
 */
export function useElevatedFetch() {
  const { requestToken } = useElevationContext();

  return useCallback(
    async <T,>(
      scope: Permission,
      path: string,
      body: unknown,
      method: 'POST' | 'PATCH' | 'PUT' | 'DELETE' = 'POST',
    ): Promise<T> => {
      try {
        return await apiFetch<T>(path, { method, body });
      } catch (error) {
        if (error instanceof ApiError && error.code === 'AUTHZ.ELEVATION_REQUIRED') {
          // 令牌绑定的目标操作 = 这次真正要重放的 method + path + body（sweep #2 M-1）
          const token = await requestToken(scope, { method, path, body });
          return await apiFetch<T>(path, { method, body, headers: { 'x-elevation-token': token } });
        }
        throw error;
      }
    },
    [requestToken],
  );
}
